import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, Check, Coins, Gift, Heart, Info, LoaderCircle, Sparkles, UserRound } from 'lucide-react';
import { DONATE_CONFIG } from '../../config';
import styles from './StoreCheckout.module.css';

const formatNumber = (value) => Number(value).toLocaleString('ru-RU');

function readNickname() {
  try { return localStorage.getItem('iw_nickname') || ''; } catch { return ''; }
}

function useNickname() {
  const [nickname, setNickname] = useState(readNickname);
  const updateNickname = (value) => {
    setNickname(value);
    try { localStorage.setItem('iw_nickname', value); } catch {}
  };
  return [nickname, updateNickname];
}

export function SupportCheckout({ optionData, loading, onPayment, paymentError }) {
  const [amount, setAmount] = useState('');
  const [nickname, setNickname] = useNickname();
  const [error, setError] = useState('');
  const minAmount = optionData.minAmount ?? 1;

  const submit = (event) => {
    event.preventDefault();
    if (loading) return;
    const sum = Number(amount);
    if (!Number.isFinite(sum) || sum < minAmount) {
      setError(`Укажите сумму не менее ${minAmount} ₽`);
      return;
    }
    setError('');
    onPayment({ type: 'donation', sum, nickname: nickname.trim() || 'anonymous' });
  };

  return (
    <form className={`${styles.form} ${styles.supportForm}`} onSubmit={submit}>
      <div className={styles.heading}>
        <span className={styles.supportEmblem}><Heart size={32} strokeWidth={1.5} aria-hidden="true" /></span>
        <span className={styles.eyebrow}>Часть большого приключения</span>
        <h2>Поддержать InterWorld</h2>
        <p>Если вам нравится проект, вы можете поддержать его развитие любой суммой.</p>
      </div>
      <div className={styles.fields}>
        <div className={styles.field}>
          <label htmlFor="support-amount">Сумма поддержки</label>
          <div className={`${styles.inputWrap} ${error ? styles.inputInvalid : ''}`}>
            <input id="support-amount" inputMode="decimal" placeholder="Например, 500"
              value={amount} disabled={loading} aria-invalid={Boolean(error)}
              aria-describedby={error ? 'support-error' : undefined}
              onChange={(event) => {
                const value = event.target.value.replace(',', '.');
                if (value === '' || /^\d*\.?\d*$/.test(value)) { setAmount(value); setError(''); }
              }} />
            <span className={styles.inputSuffix}>₽</span>
          </div>
          {error && <p id="support-error" className={styles.error} role="alert">{error}</p>}
        </div>
        <div className={styles.field}>
          <label htmlFor="support-nickname">Никнейм <span>необязательно</span></label>
          <div className={styles.inputWrap}>
            <UserRound size={18} aria-hidden="true" />
            <input id="support-nickname" placeholder="Ваш ник в игре" value={nickname}
              disabled={loading} spellCheck={false} autoComplete="off"
              onChange={(event) => setNickname(event.target.value)} />
          </div>
        </div>
      </div>
      {paymentError && <p className={styles.error} role="alert">{paymentError}</p>}
      <div className={styles.actionRow}>
        <p className={styles.helper}>Можно поддержать проект без указания ника.</p>
        <button className={styles.primaryButton} type="submit" disabled={loading}>
          {loading ? <><LoaderCircle size={18} className={styles.spinner} aria-hidden="true" /> Переходим к оплате…</>
            : <>Поддержать{Number(amount) > 0 ? ` · ${formatNumber(amount)} ₽` : ''}<ArrowRight size={18} aria-hidden="true" /></>}
        </button>
      </div>
    </form>
  );
}

const packageArt = {
  dc_pack_100: '/images/store/tokens-100.webp',
  dc_pack_250: '/images/store/tokens-250.webp',
  dc_pack_500: '/images/store/tokens-500.webp',
  dc_pack_1000: '/images/store/tokens-1000.webp',
  dc_pack_2500: '/images/store/tokens-2500.webp',
  dc_pack_5000: '/images/store/tokens-5000.webp',
};

export default function StoreCheckout({ packages, loading, onPayment, paymentError }) {
  const [nickname, setNickname] = useNickname();
  const [nickError, setNickError] = useState('');
  const [checking, setChecking] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const checkRef = useRef(null);
  const nicknameRef = useRef(null);
  const busy = checking || loading;
  const maxBonus = Math.max(0, ...packages.map((pkg) => pkg.bonus || 0));
  const bonusTokens = selectedPackage
    ? Math.max(0, selectedPackage.currencyAmount - selectedPackage.price * DONATE_CONFIG.donateCurrencyRate)
    : 0;

  useEffect(() => () => checkRef.current?.abort(), []);

  const submit = async (event) => {
    event.preventDefault();
    if (busy || checkRef.current || !selectedPackage || !agreed) return;
    const nick = nickname.trim();
    setNickError('');
    if (!nick) {
      setNickError('Укажите никнейм игрока.');
      nicknameRef.current?.focus();
      return;
    }
    const controller = new AbortController();
    checkRef.current = controller;
    setChecking(true);
    try {
      const response = await fetch(`${DONATE_CONFIG.donateCurrencyCheckUrl}?nickname=${encodeURIComponent(nick)}`, {
        headers: { 'X-API-KEY': DONATE_CONFIG.apiKey }, signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      if (response.status === 404) {
        setNickError('Игрок не найден. Зайдите на сервер хотя бы один раз и проверьте написание ника.');
        nicknameRef.current?.focus();
      } else if (!response.ok) {
        setNickError('Не удалось проверить игрока. Попробуйте ещё раз.');
      } else {
        await onPayment({
          type: 'donate_currency', sum: selectedPackage.price.toFixed(2),
          packKey: selectedPackage.key, nickname: nick,
        });
      }
    } catch (error) {
      if (error.name !== 'AbortError') setNickError('Не удалось подключиться. Проверьте интернет и попробуйте ещё раз.');
    } finally {
      if (checkRef.current === controller) {
        checkRef.current = null;
        setChecking(false);
      }
    }
  };

  return (
    <div className={styles.storeGrid}>
      <div className={styles.catalog}>
        <div className={styles.catalogHeading}>
          <div className={styles.heading}>
            <span className={styles.eyebrow}>Для новых приключений</span>
            <h2>Заряжай свой мир</h2>
            <p>Выбирай пакет IW-Токенов и пополняй игровой баланс.</p>
          </div>
          {maxBonus > 0 && <div className={styles.bonusCallout}>
            <Sparkles size={18} aria-hidden="true" />
            <span>Бонус до <strong>+{maxBonus}%</strong></span>
          </div>}
        </div>

        <fieldset className={styles.packageFieldset} disabled={busy} aria-describedby="package-hint">
          <legend className={styles.visuallyHidden}>Выберите пакет IW-Токенов</legend>
          <div className={styles.packages}>
            {packages.map((pkg) => {
              const selected = selectedPackage?.key === pkg.key;
              return (
                <label key={pkg.key} className={styles.packageOption}>
                  <input type="radio" name="token-package" value={pkg.key}
                    checked={selected} onChange={() => setSelectedPackage(pkg)} />
                  <span className={`${styles.packageCard} ${pkg.bonus === maxBonus && maxBonus > 0 ? styles.maxBonusCard : ''}`}>
                    <span className={styles.packageTop}>
                      {pkg.bonus > 0 ? <span className={styles.bonus}>+{pkg.bonus}% бонус</span>
                        : <span className={styles.baseLabel}>Базовый пакет</span>}
                      <span className={styles.selectionMark} aria-hidden="true"><Check size={13} /></span>
                    </span>
                    <span className={styles.packageArtwork} aria-hidden="true">
                      {packageArt[pkg.key] ? <img src={packageArt[pkg.key]} alt="" width="320" height="320" draggable="false" />
                        : <Coins size={96} strokeWidth={1} />}
                    </span>
                    <span className={styles.packageDetails}>
                      <strong className={styles.tokenAmount}>{formatNumber(pkg.currencyAmount)}</strong>
                      <span className={styles.tokenLabel}>IW-Токенов</span>
                    </span>
                    <span className={styles.packageBottom}>
                      <strong>{formatNumber(pkg.price)} <span>₽</span></strong>
                      <span className={styles.selectLabel}>{selected ? 'Выбрано' : 'Выбрать'}<ArrowRight size={14} aria-hidden="true" /></span>
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>
        <p id="package-hint" className={styles.catalogHint}><Info size={15} aria-hidden="true" />Бонус уже включён в количество токенов. Чем больше пакет — тем выгоднее курс.</p>
      </div>

      <form className={styles.checkoutCard} onSubmit={submit} aria-label="Оформление покупки" aria-busy={busy}>
        <div className={styles.checkoutHeading}>
          <span className={styles.checkoutIcon}><Coins size={21} aria-hidden="true" /></span>
          <div><span className={styles.eyebrow}>Почти в игре</span><h2>Ваш заказ</h2></div>
        </div>
        <div className={styles.field}>
          <label htmlFor="token-nickname">Никнейм игрока</label>
          <div className={`${styles.inputWrap} ${nickError ? styles.inputInvalid : ''}`}>
            <UserRound size={18} aria-hidden="true" />
            <input ref={nicknameRef} id="token-nickname" placeholder="Введите ник в игре" value={nickname}
              spellCheck={false} autoComplete="off" autoCapitalize="none" disabled={busy}
              aria-invalid={Boolean(nickError)} aria-describedby={`nickname-hint${nickError ? ' nickname-error' : ''}`}
              onChange={(event) => { setNickname(event.target.value); setNickError(''); }} />
          </div>
          <p id="nickname-hint" className={styles.hint}>Игрок должен хотя бы раз зайти на сервер.</p>
          {nickError && <p id="nickname-error" className={styles.error} role="alert">{nickError}</p>}
        </div>

        <div className={styles.orderSummary} aria-live="polite" aria-atomic="true">
          {selectedPackage ? <>
            <div className={styles.selectedProduct}>
              {packageArt[selectedPackage.key] && <img src={packageArt[selectedPackage.key]} alt="" width="68" height="68" />}
              <div><strong>{formatNumber(selectedPackage.currencyAmount)}</strong><span>IW-Токенов</span></div>
              <Check className={styles.productCheck} size={18} aria-hidden="true" />
            </div>
            {bonusTokens > 0 && <div className={styles.bonusRow}><span>Из них в подарок</span><strong>+{formatNumber(bonusTokens)} IW</strong></div>}
          </> : <div className={styles.emptyOrder}><Coins size={26} strokeWidth={1.5} aria-hidden="true" /><span>Выберите пакет<span>Он появится здесь</span></span></div>}
          <div className={styles.total}><span>К оплате</span><strong>{selectedPackage ? <>{formatNumber(selectedPackage.price)} <span>₽</span></> : '—'}</strong></div>
        </div>

        <label className={styles.agreement}>
          <span className={styles.agreementControl}>
            <input type="checkbox" checked={agreed} disabled={busy} onChange={(event) => setAgreed(event.target.checked)} />
            <span className={styles.agreementCheck} aria-hidden="true"><Check size={15} strokeWidth={2.5} /></span>
          </span>
          <span>Я подтверждаю, что мне исполнилось 18 лет (либо у меня есть согласие законных представителей), и я принимаю условия{' '}
            <a href="/public-offer" target="_blank" rel="noreferrer" onClick={(event) => event.stopPropagation()}>Публичной оферты</a> и{' '}
            <a href="/server-rules" target="_blank" rel="noreferrer" onClick={(event) => event.stopPropagation()}>Правил сервера</a>.
          </span>
        </label>
        {paymentError && <p className={styles.error} role="alert">{paymentError}</p>}
        <button type="submit" className={styles.primaryButton} disabled={busy || !selectedPackage || !agreed}>
          {busy ? <><LoaderCircle size={18} className={styles.spinner} aria-hidden="true" />{loading ? 'Переходим к оплате…' : 'Проверяем игрока…'}</>
            : <>Перейти к оплате<ArrowRight size={18} aria-hidden="true" /></>}
        </button>
        <p className={styles.giftHint}><Gift size={16} aria-hidden="true" /><span>Токены в подарок другу?<br />Просто укажите его никнейм.</span></p>
      </form>
    </div>
  );
}
